import { templateService } from "@/services/template.service";
import { useState, useEffect, useRef, FC } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Trash2, ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TemplateElement, TemplateStyle, TemplateElementType } from "./template.model";
import { CSSProperties } from "react";
import { ScaledPreview } from "@/components/scaled-preview/scaled-preview";
// Import scaling utilities
import {
    updateImageProperties,
    getOpacityFromRgba,
    hexToRgba,
    getFontSize,
} from "@/components/scaled-preview/scaling-utils";
// Import the em-based positioning utilities
import {
    pageToEmCoordinates,
    applyEmSnapping,
    updateElementPositionEm,
    convertPercentToEmPosition,
    generateEmSnapPoints,
} from "@/components/scaled-preview/em-positioning";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { useSaveTemplate } from "@/api/template.api";
import { TemplateDto } from "@exibra/shared";

// Simple UUID generation function
const generateUUID = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

// Update the styles object
const styles = {
    editorContainer: "flex gap-4 p-4 bg-background",
    leftPanel: "w-[320px] space-y-4",
    previewSection: "flex-1 flex flex-col min-h-0",
    formGroup: "space-y-2",
    textField: "min-w-[100px] p-2 focus:outline-none bg-transparent text-center",
    templateWrapper: "relative w-full h-full flex items-center justify-center",
    templateContent: "absolute inset-0 bg-background",
};

const defaultPreset: Partial<TemplateDto> = {
    // TODO: fix this
    // @ts-expect-error - needs to build for now
    id: "modern-card",
    name: "Modern Card Layout",
    elements: [],
    style: {
        textColor: "#ffffff",
        backgroundColor: "#000000",
        cardBackgroundColor: "rgba(0, 0, 0, 0.5)",
    },
};

const presets: Partial<TemplateDto>[] = [
    defaultPreset,
    {
        // TODO: fix this
        // @ts-expect-error - needs to build for now
        id: "centered-content",
        name: "Centered Content",
        description: "Text centered on the page",
        elements: [
            {
                type: "text",
                content: "Main Title",
                id: generateUUID(),
                style: {
                    position: "absolute",
                    top: "4.5em",
                    left: "8em",
                    transform: "translate(-50%, -50%)",
                    fontSize: "5em",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                } as CSSProperties,
            },
            {
                type: "text",
                content: "Subtitle Text",
                id: generateUUID(),
                style: {
                    position: "absolute",
                    top: "6em",
                    left: "8em",
                    transform: "translate(-50%, -50%)",
                    fontSize: "2.5em",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                } as CSSProperties,
            },
        ],
        style: {
            textColor: "#ffffff",
            backgroundColor: "#000000",
            cardBackgroundColor: "rgba(0, 0, 0, 0.5)",
        },
    },
];

/**
 * Creates a text element style with standardized defaults
 * @param baseStyle Existing style object or partial style
 * @param options Additional style options to apply
 */
const createTextElementStyle = (
    baseStyle: CSSProperties = {},
    options: {
        fontSize?: number;
        fontWeight?: string;
        fontStyle?: string;
        textDecoration?: string;
        color?: string;
        textAlign?: "left" | "center" | "right" | "justify";
    } = {},
): CSSProperties => {
    // Start with position defaults for text elements (center-based)
    const style: CSSProperties = {
        position: "absolute",
        top: baseStyle.top || "4.5em", // Center vertically (50% of 9em)
        left: baseStyle.left || "8em", // Center horizontally (50% of 16em)
        transform: "translate(-50%, -50%)", // Center the element
        whiteSpace: baseStyle.whiteSpace || "nowrap",
        textAlign: (baseStyle.textAlign as "left" | "center" | "right" | "justify") || "center",
        // Font styling with defaults
        fontWeight: baseStyle.fontWeight || "normal",
        fontStyle: baseStyle.fontStyle || "normal",
        textDecoration: baseStyle.textDecoration || "none",
        // Set a high z-index by default to ensure text is always on top
        zIndex: 1000,
        // Override with any existing style properties
        ...baseStyle,
    };

    // Apply fontSize if provided in options or if not already set
    if (options.fontSize || !style.fontSize) {
        style.fontSize = `${options.fontSize || 1.5}em`;
    }

    // Apply other options if provided
    if (options.fontWeight) style.fontWeight = options.fontWeight;
    if (options.fontStyle) style.fontStyle = options.fontStyle;
    if (options.textDecoration) style.textDecoration = options.textDecoration;
    if (options.color) style.color = options.color;
    if (options.textAlign) style.textAlign = options.textAlign;

    return style;
};

/**
 * Props for the TemplateEditor component
 */
interface TemplateEditorProps {
    /** Whether the template is being created or edited */
    isCreate?: boolean;

    /** The template to edit */
    template?: TemplateDto;
}

export const TemplateEditor: FC<TemplateEditorProps> = ({ isCreate = false, template }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const selectedTenant = useTenantStore((state) => state.selectedTenant);

    // Ensure all elements have IDs in the initial template
    const ensureElementIds = (template: Partial<TemplateDto>): Partial<TemplateDto> => {
        return {
            ...template,
            elements: template.elements?.map((el) => ({
                ...el,
                id: el.id || generateUUID(),
            })),
        };
    };

    const { mutateAsync: saveTemplate } = useSaveTemplate();

    const initialTemplate = ensureElementIds(location.state?.template || defaultPreset);

    const [selectedPreset, setSelectedPreset] = useState<Partial<TemplateDto>>(
        template ?? initialTemplate,
    );
    const [elements, setElements] = useState<TemplateElement[]>(template?.elements ?? []);
    const [templateStyle, setTemplateStyle] = useState<TemplateStyle>(
        template?.style ?? ({} as TemplateStyle),
    );
    const [templateName, setTemplateName] = useState(template?.name ?? initialTemplate.name);
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
    const [selectedTextIds, setSelectedTextIds] = useState<string[]>([]);
    const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
    const [imageStyle, setImageStyle] = useState({
        scale: 1,
        opacity: 1,
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const previewAreaRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [draggingElementType, setDraggingElementType] = useState<TemplateElementType | null>(
        null,
    );

    // Define em-based constants
    const EM_SNAP_THRESHOLD = 0.5;
    const [emSnapPoints] = useState(generateEmSnapPoints(4));
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Sync state when selected preset changes
    useEffect(() => {
        if (selectedPreset) {
            setElements(selectedPreset.elements ?? []);
            setTemplateStyle(selectedPreset.style ?? ({} as TemplateStyle));
        }
    }, [selectedPreset]);

    // const handlePresetChange = (presetId: string) => {
    //     const preset = [...presets].find((p) => p.id === presetId);
    //     if (preset) {
    //         const presetWithIds = ensureElementIds(preset);
    //         setSelectedPreset(presetWithIds);
    //     }
    // };

    const handleSaveTemplate = async () => {
        const newTemplate: Partial<TemplateDto> = {
            ...selectedPreset,
            id: isCreate ? undefined : template?.id,
            name: templateName || "Untitled Template",
            elements: elements,
            style: templateStyle,
            isCustom: true,
            tenantId: selectedTenant?.id,
        };

        await saveTemplate({ template: newTemplate as TemplateDto, isCreate });
        navigate("..");
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                const imageUrl = await templateService.uploadImage(file);

                // Create base image element with center-point positioning
                const newImageElement: TemplateElement = {
                    type: "image",
                    content: imageUrl,
                    id: generateUUID(),
                    style: {
                        width: "8em", // 50% width in em units (16/2)
                        height: "4.5em", // 50% height in em units (9/2)
                        objectFit: "cover",
                        position: "absolute",
                        top: "4.5em", // center vertically (9/2)
                        left: "8em", // center horizontally (16/2)
                        transform: "translate(-50%, -50%)", // Center the image
                        opacity: imageStyle.opacity,
                    } as CSSProperties,
                };

                setElements((prev) => [...prev, newImageElement]);
                setSelectedImageId(newImageElement.id);
                toast.success("Image uploaded successfully!");
            } catch (error) {
                console.error("Error uploading image:", error);
                toast.error("Failed to upload image. Please try again.");
            }
        }
    };

    // Update the handleMouseDown function to calculate drag offset for text elements
    const handleMouseDown = (
        e: React.MouseEvent,
        mode: "drag",
        elementType?: TemplateElementType,
    ) => {
        // Prevent default to avoid text selection while dragging
        e.preventDefault();

        // Get the appropriate reference for position calculation
        const parentElement =
            elementType === "container"
                ? containerRef.current?.parentElement
                : previewAreaRef.current;

        if (!parentElement) return;

        const rect = parentElement.getBoundingClientRect();
        if (!rect) return;

        // Calculate starting position in em units
        const startEmPos = pageToEmCoordinates(e.clientX, e.clientY, rect);

        if (mode === "drag") {
            // Set dragging state and type to begin tracking movement
            setIsDragging(true);
            setDraggingElementType(elementType || null);

            // For text elements, calculate drag offset to maintain position relative to click
            if (elementType === "text") {
                const target = e.currentTarget as HTMLElement;
                const textId = target.getAttribute("data-id");

                if (textId) {
                    // Prevent event propagation to avoid deselection
                    e.stopPropagation();

                    // Find the element to get its current position
                    const textElement = elements.find((el) => el.id === textId);

                    if (textElement) {
                        // Get the element's current position from style
                        const currentLeft = parseFloat(
                            String(textElement.style?.left || "8em").replace("em", ""),
                        );
                        const currentTop = parseFloat(
                            String(textElement.style?.top || "4.5em").replace("em", ""),
                        );

                        // Calculate and set the offset between the current position and click position
                        setDragOffset({
                            x: currentLeft - startEmPos.x,
                            y: currentTop - startEmPos.y,
                        });
                    }

                    // If not already selected, select this text element
                    if (!selectedTextIds.includes(textId)) {
                        setSelectedTextIds([textId]);
                        setSelectedContainerId(null);
                        setSelectedImageId(null);
                    }
                }
            } else {
                // Reset drag offset for other element types
                setDragOffset({ x: 0, y: 0 });
            }
        }
    };

    const handleImageClick = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedImageId(id);
        setSelectedContainerId(null);
        setSelectedTextIds([]);
    };

    // Update handleImageDrag to use center-point positioning
    const handleImageDrag = (e: MouseEvent) => {
        if (!isDragging || !previewAreaRef.current || !selectedImageId) return;

        const parentRect = previewAreaRef.current.getBoundingClientRect();
        if (!parentRect) return;

        // Calculate position in em units
        const emPosition = pageToEmCoordinates(e.clientX, e.clientY, parentRect);

        // Apply snapping with em-based snap points
        const { x: snappedX, y: snappedY } = applyEmSnapping(
            emPosition,
            EM_SNAP_THRESHOLD,
            emSnapPoints,
        );

        // Update element position using center-point positioning
        setElements((prev) =>
            prev.map((el) => {
                if (el.id === selectedImageId) {
                    return {
                        ...el,
                        style: {
                            ...el.style,
                            left: `${snappedX}em`,
                            top: `${snappedY}em`,
                            transform: "translate(-50%, -50%)", // Center the image
                        },
                    };
                }
                return el;
            }),
        );
    };

    // Update handleMouseMove to handle text element snapping from center point
    const handleMouseMove = (e: MouseEvent) => {
        if ((!containerRef.current && !previewAreaRef.current) || !isDragging) return;

        if (selectedImageId) {
            handleImageDrag(e);
            return;
        }

        const parentElement =
            draggingElementType === "container"
                ? containerRef.current?.parentElement
                : previewAreaRef.current;

        const parentRect = parentElement?.getBoundingClientRect();
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!parentRect) return;

        if (isDragging) {
            // Calculate mouse position in em units
            const emPosition = pageToEmCoordinates(e.clientX, e.clientY, parentRect);

            // For text elements, we're already using the center point since transform is applied
            const adjustedPosition = {
                x: draggingElementType === "text" ? emPosition.x + dragOffset.x : emPosition.x,
                y: draggingElementType === "text" ? emPosition.y + dragOffset.y : emPosition.y,
            };

            // Apply snapping with em-based snap points
            const { x: snappedX, y: snappedY } = applyEmSnapping(
                adjustedPosition,
                EM_SNAP_THRESHOLD,
                emSnapPoints,
            );

            if (draggingElementType === "container" && containerRect) {
                setElements((prev) =>
                    prev.map((el) =>
                        el.id === selectedContainerId
                            ? updateElementPositionEm(el, { x: snappedX, y: snappedY }, "container")
                            : el,
                    ),
                );
            } else if (draggingElementType === "text") {
                if (selectedTextIds.length > 0) {
                    setElements((prev) =>
                        prev.map((el) => {
                            if (selectedTextIds.includes(el.id!)) {
                                // For text elements, the position is already the center point
                                return {
                                    ...el,
                                    style: {
                                        ...el.style,
                                        left: `${snappedX}em`,
                                        top: `${snappedY}em`,
                                    },
                                };
                            }
                            return el;
                        }),
                    );
                }
            }
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setDraggingElementType(null);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
            return () => {
                window.removeEventListener("mousemove", handleMouseMove);
                window.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [isDragging]);

    const handleAddContainer = () => {
        const newContainer: TemplateElement = {
            type: "container",
            content: "",
            id: generateUUID(),
            style: {
                position: "absolute",
                left: "8em",
                top: "4.5em",
                transform: "translate(-50%, -50%)",
                width: "8em",
                height: "4.5em",
                backgroundColor: "#ffffff",
                background: "rgba(255, 255, 255, 0.5)",
                opacity: 0.5,
                borderRadius: "0.5em",
                zIndex: elements.length, // Set initial z-index based on order
            } as CSSProperties,
        };
        setElements((prev) => [...prev, newContainer]);
        handleElementSelection("container", newContainer.id);
    };

    const handleContainerClick = (containerId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        // Only allow selection if the element is already selected in the sidebar
        if (selectedContainerId === containerId) {
            setSelectedContainerId(containerId);
        }
    };

    const handleAddTextElement = () => {
        const newElement: TemplateElement = {
            type: "text",
            content: "New Text",
            id: generateUUID(),
            style: createTextElementStyle(),
        };
        setElements((prev) => [...prev, newElement]);
        handleElementSelection("text", newElement.id);
    };

    const handleTextElementClick = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();

        // When clicking on a text element in the preview:
        // 1. If it's not already selected in the sidebar, select it in the sidebar
        // 2. If it's already selected, prepare for editing (this behavior can be expanded later)
        if (!selectedTextIds.includes(id)) {
            handleElementSelection("text", id);
        } else {
            // The element is already selected, user might want to edit it
            // (this is where you could add additional functionality if needed)
            setSelectedTextIds([id]); // Ensure it's the only selected text
        }
    };

    // Add this function to handle container collapse
    const handleElementSelection = (type: "text" | "image" | "container", id: string) => {
        if (type === "container") {
            setSelectedContainerId(id);
            setSelectedTextIds([]);
            setSelectedImageId(null);
        } else if (type === "text") {
            setSelectedTextIds([id]);
            setSelectedContainerId(null);
            setSelectedImageId(null);
        } else if (type === "image") {
            setSelectedImageId(id);
            setSelectedContainerId(null);
            setSelectedTextIds([]);
        }
    };

    // Helper function to toggle text style
    const toggleTextStyle = (
        textId: string,
        property: "fontWeight" | "fontStyle" | "textDecoration",
        value: string,
    ) => {
        setElements((prev) =>
            prev.map((el) => {
                if (el.id !== textId) return el;

                // Find the text element to update
                const textElement = el as TemplateElement & { type: "text" };

                // Determine the new property value (toggle between value and default)
                const currentValue = (textElement.style as Record<string, string>)[property];
                const newValue =
                    currentValue === value
                        ? property === "textDecoration"
                            ? "none"
                            : "normal"
                        : value;

                // Create updated style with the toggled property
                const options: Record<string, string> = {};
                options[property] = newValue;

                return {
                    ...textElement,
                    style: {
                        ...textElement.style,
                        ...options,
                    },
                };
            }),
        );
    };

    // Update the moveToBackground function to maintain relative order
    const moveToBackground = (elementId: string) => {
        setElements((prev) => {
            // Get all elements except the one being moved
            const otherElements = prev.filter((el) => el.id !== elementId);
            const elementToMove = prev.find((el) => el.id === elementId);

            if (!elementToMove) return prev;

            // Find the lowest z-index among elements
            const lowestZIndex = Math.min(
                ...otherElements.map((el) => Number(el.style?.zIndex) || 0),
            );

            // Move all elements up by 1 to make room at the bottom
            const updatedOthers = otherElements.map((el) => ({
                ...el,
                style: {
                    ...el.style,
                    zIndex: (Number(el.style?.zIndex) || 0) + 1,
                },
            }));

            // Place the moved element at the bottom
            const updatedElement = {
                ...elementToMove,
                style: {
                    ...elementToMove.style,
                    zIndex: lowestZIndex,
                },
            };

            return [...updatedOthers, updatedElement];
        });
    };

    // Update the control sections to include the move to background button
    const renderElementControls = (element: TemplateElement) => {
        const isSelected =
            (element.type === "text" && selectedTextIds.includes(element.id)) ||
            (element.type === "container" && selectedContainerId === element.id) ||
            (element.type === "image" && selectedImageId === element.id);

        if (!isSelected) return null;

        return (
            <div className="mt-2 flex items-center gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => moveToBackground(element.id)}
                    className="flex items-center gap-1"
                >
                    <ArrowDown className="h-3 w-3" />
                    <span className="text-xs">Move to Back</span>
                </Button>
            </div>
        );
    };

    // Update the image element card to include only scale control
    const renderImageControls = (imageElement: TemplateElement) => {
        if (selectedImageId !== imageElement.id) return null;

        // Extract current scale and opacity from the element
        let currentScale = 1;
        if (imageElement.style?.transform) {
            const scaleMatch = imageElement.style.transform.match(/scale\(([^)]+)\)/);
            if (scaleMatch && scaleMatch[1]) {
                currentScale = parseFloat(scaleMatch[1]);
            }
        }
        const currentOpacity =
            imageElement.style?.opacity !== undefined ? Number(imageElement.style.opacity) : 1;

        // Update local state to match element's current values
        if (imageStyle.scale !== currentScale || imageStyle.opacity !== currentOpacity) {
            setImageStyle({
                scale: currentScale,
                opacity: currentOpacity,
            });
        }

        return (
            <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label className="text-xs">Scale</Label>
                        <Input
                            type="range"
                            min="0.1"
                            max="2"
                            step="0.1"
                            value={currentScale}
                            onChange={(e) => {
                                const newScale = parseFloat(e.target.value);
                                setImageStyle((prev) => ({ ...prev, scale: newScale }));
                                setElements((prev) =>
                                    prev.map((el) =>
                                        el.id === imageElement.id
                                            ? updateImageProperties(el, { scale: newScale })
                                            : el,
                                    ),
                                );
                            }}
                            className="h-2"
                        />
                        <div className="text-xs text-muted-foreground">
                            Scale: {currentScale.toFixed(1)}x
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs">Opacity</Label>
                        <Input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={currentOpacity}
                            onChange={(e) => {
                                const newOpacity = parseFloat(e.target.value);
                                setImageStyle((prev) => ({ ...prev, opacity: newOpacity }));
                                setElements((prev) =>
                                    prev.map((el) =>
                                        el.id === imageElement.id
                                            ? updateImageProperties(el, { opacity: newOpacity })
                                            : el,
                                    ),
                                );
                            }}
                            className="h-2"
                        />
                        <div className="text-xs text-muted-foreground">
                            Opacity: {currentOpacity.toFixed(1)}
                        </div>
                    </div>
                </div>
                {renderElementControls(imageElement)}
            </div>
        );
    };

    // Update the container controls section to work with em units
    const renderContainerControls = (container: TemplateElement & { type: "container" }) => {
        // Get container dimensions from em units
        const width = parseFloat(container.style?.width?.toString().replace("em", "") || "8");
        const height = parseFloat(container.style?.height?.toString().replace("em", "") || "4.5");

        // Convert em dimensions to percentage for the slider (0-100)
        const widthPercentage = (width / 16) * 100;
        const heightPercentage = (height / 9) * 100;

        // Calculate maximum possible border radius based on container dimensions
        const maxPossibleRadius = Math.min(width, height) / 2;

        // Get current border radius value - handle both number and em string formats
        let currentRadius = 0;
        if (container.style?.borderRadius) {
            const radiusStr = container.style.borderRadius.toString();
            currentRadius = radiusStr.includes("em")
                ? parseFloat(radiusStr.replace("em", ""))
                : parseFloat(radiusStr);
        }

        // Convert current radius to slider percentage (relative to max possible radius)
        const sliderValue = (currentRadius / maxPossibleRadius) * 100;

        // Get current opacity from background or use default
        const currentOpacity = getOpacityFromRgba(container.style?.background?.toString());

        return (
            <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs">Height (%)</Label>
                        <Input
                            type="range"
                            min="10"
                            max="100"
                            value={heightPercentage}
                            onChange={(e) => {
                                const percentage = parseInt(e.target.value);
                                // Convert percentage to em units
                                const emValue = (percentage / 100) * 9;
                                setElements((prev) =>
                                    prev.map((el) =>
                                        el.id === container.id
                                            ? {
                                                  ...el,
                                                  style: {
                                                      ...el.style,
                                                      height: `${emValue}em`,
                                                  },
                                              }
                                            : el,
                                    ),
                                );
                            }}
                        />
                        <div className="text-xs text-muted-foreground mt-1">
                            {Math.round(heightPercentage)}%
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Width (%)</Label>
                        <Input
                            type="range"
                            min="10"
                            max="100"
                            value={widthPercentage}
                            onChange={(e) => {
                                const percentage = parseInt(e.target.value);
                                // Convert percentage to em units
                                const emValue = (percentage / 100) * 16;
                                setElements((prev) =>
                                    prev.map((el) =>
                                        el.id === container.id
                                            ? {
                                                  ...el,
                                                  style: {
                                                      ...el.style,
                                                      width: `${emValue}em`,
                                                  },
                                              }
                                            : el,
                                    ),
                                );
                            }}
                        />
                        <div className="text-xs text-muted-foreground mt-1">
                            {Math.round(widthPercentage)}%
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Border Radius</Label>
                        <Input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={Math.min(sliderValue, 100)}
                            onChange={(e) => {
                                const percentage = parseInt(e.target.value);
                                // Convert slider percentage to actual radius value in em units
                                const newRadius = (percentage / 100) * maxPossibleRadius;
                                setElements((prev) =>
                                    prev.map((el) =>
                                        el.id === container.id
                                            ? {
                                                  ...el,
                                                  style: {
                                                      ...el.style,
                                                      borderRadius: `${newRadius}em`, // Apply as em units
                                                  },
                                              }
                                            : el,
                                    ),
                                );
                            }}
                        />
                        <div className="text-xs text-muted-foreground mt-1">
                            {Math.round(Math.min(sliderValue, 100))}%
                        </div>
                    </div>

                    {/* Rest of the existing controls remain the same */}
                    <div>
                        <Label className="text-xs">Transparency</Label>
                        <Input
                            type="range"
                            min="0"
                            max="100"
                            value={100 - currentOpacity * 100}
                            onChange={(e) => {
                                const opacity = 1 - parseInt(e.target.value) / 100;
                                const color =
                                    container.style?.backgroundColor?.toString() || "#ffffff";
                                setElements((prev) =>
                                    prev.map((el) =>
                                        el.id === container.id
                                            ? {
                                                  ...el,
                                                  style: {
                                                      ...el.style,
                                                      background: hexToRgba(color, opacity),
                                                      opacity: opacity,
                                                  },
                                              }
                                            : el,
                                    ),
                                );
                            }}
                        />
                        <div className="text-xs text-muted-foreground mt-1">
                            {Math.round(100 - currentOpacity * 100)}%
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Background Color</Label>
                        <div className="flex gap-2 items-center">
                            <Input
                                type="color"
                                className="w-12 h-8"
                                value={container.style?.backgroundColor?.toString() || "#ffffff"}
                                onChange={(e) => {
                                    const color = e.target.value;
                                    const opacity = getOpacityFromRgba(
                                        container.style?.background?.toString(),
                                    );
                                    setElements((prev) =>
                                        prev.map((el) =>
                                            el.id === container.id
                                                ? {
                                                      ...el,
                                                      style: {
                                                          ...el.style,
                                                          backgroundColor: color,
                                                          background: hexToRgba(color, opacity),
                                                      },
                                                  }
                                                : el,
                                        ),
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Move Element</Label>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveToBackground(container.id)}
                            className="flex items-center gap-1 h-8 w-full"
                        >
                            <ArrowDown className="h-3 w-3" />
                            <span className="text-xs">Move to Back</span>
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // Add a useEffect to convert existing percentage-based positions to em-based positions
    useEffect(() => {
        // Convert any percentage-based positions to em-based positions
        if (elements.length > 0) {
            const convertedElements = elements.map((element) =>
                convertPercentToEmPosition(element),
            );

            // Only update if there's a difference
            const needsUpdate = convertedElements.some((el, index) => {
                const oldStyle = elements[index]?.style;
                const newStyle = el.style;

                if (oldStyle && newStyle) {
                    return oldStyle.left !== newStyle.left || oldStyle.top !== newStyle.top;
                }
                return false;
            });

            if (needsUpdate) {
                setElements(convertedElements);
            }
        }
    }, []);

    // Update the renderTextElement function to handle em-based positioning
    const renderTextElement = (textElement: any) => {
        const isSelected = selectedTextIds.includes(textElement.id!);

        // Extract position and styling properties
        const { style = {} } = textElement;
        const {
            left = "8em",
            top = "4.5em",
            fontSize = "1.6em",
            fontWeight,
            fontStyle,
            textDecoration,
            color,
            textAlign = "center",
            ...otherStyles
        } = style;

        return (
            <div
                key={textElement.id}
                data-id={textElement.id}
                className={cn("absolute cursor-move", isSelected && "ring-2 ring-primary")}
                style={{
                    left,
                    top,
                    position: "absolute",
                    transform: "translate(-50%, -50%)", // Center the text element
                }}
                onClick={(e) => handleTextElementClick(textElement.id!, e)}
                onMouseDown={(e) => handleMouseDown(e, "drag", "text")}
            >
                <div
                    style={{
                        fontSize,
                        fontWeight,
                        fontStyle,
                        textDecoration,
                        color,
                        textAlign,
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        ...otherStyles,
                    }}
                >
                    {textElement.content}
                </div>
            </div>
        );
    };

    // Update renderContainerElement to handle em-based positioning
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const renderContainerElement = (container: any) => {
        const isSelected = container.id === selectedContainerId;

        // Apply the style directly since it's already in em units
        return (
            <div
                ref={isSelected ? containerRef : undefined}
                key={container.id}
                className={cn(
                    "absolute cursor-move",
                    isSelected && "ring-2 ring-primary ring-offset-2",
                )}
                style={container.style}
                onClick={(e) => {
                    e.stopPropagation();
                    handleContainerClick(container.id!, e);
                }}
                onMouseDown={(e) => handleMouseDown(e, "drag", "container")}
            />
        );
    };

    // Update the elements rendering to use the new renderImageElement function
    const renderImageElement = (element: TemplateElement) => {
        console.log(import.meta.env.VITE_API_URL + element.content);
        return (
            <img
                key={element.id}
                src={import.meta.env.VITE_API_URL + element.content}
                alt="Template background"
                className={cn(
                    "absolute object-cover",
                    selectedImageId === element.id && "ring-2 ring-primary",
                )}
                style={element.style}
                onClick={(e) => handleImageClick(element.id, e)}
                onMouseDown={(e) => handleMouseDown(e, "drag", "image")}
            />
        );
    };

    return (
        <div className="p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h1>{t(`templateEditor.${isCreate ? "create" : "edit"}`)}</h1>
                <Button onClick={handleSaveTemplate} className="flex items-center gap-2">
                    {t("general.save")}
                </Button>
            </div>

            <Card className="p-4">
                <div className="space-y-6">
                    <div className={styles.formGroup}>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Template Name</Label>
                                <Input
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder="Enter template name"
                                />
                            </div>
                            {/* <div> */}
                            {/* <Label>Template Presets</Label> */}
                            {/* <Select
                                    value={selectedPreset.id}
                                    onValueChange={handlePresetChange}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a template" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectLabel>Predefined Templates</SelectLabel>
                                            {presets.map((preset) => (
                                                <SelectItem key={preset.id} value={preset.id}>
                                                    {preset.name}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                        {customTemplates && customTemplates.length > 0 && (
                                            <SelectGroup>
                                                <SelectLabel>Custom Templates</SelectLabel>
                                                {customTemplates.map((template) => (
                                                    <SelectItem
                                                        key={template.id}
                                                        value={template.id}
                                                    >
                                                        {template.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectGroup>
                                        )}
                                    </SelectContent>
                                </Select> */}
                            {/* </div> */}
                            <div>
                                <Label>Background Color</Label>
                                <div className="flex gap-2 items-center">
                                    <Input
                                        type="color"
                                        className="w-12 h-8"
                                        value={templateStyle.backgroundColor || "#000000"}
                                        onChange={(e) => {
                                            setTemplateStyle((prev) => ({
                                                ...prev,
                                                backgroundColor: e.target.value,
                                            }));
                                        }}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        {templateStyle.backgroundColor || "#000000"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <h2 className="text-lg font-semibold mb-4">Template Elements</h2>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <Label>Image Elements</Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            const input = document.createElement("input");
                                            input.type = "file";
                                            input.accept = "image/*";
                                            input.onchange = (e) =>
                                                handleImageUpload(
                                                    e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                );
                                            input.click();
                                        }}
                                    >
                                        Add Image
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {elements
                                        .filter(
                                            (el): el is TemplateElement & { type: "image" } =>
                                                el.type === "image",
                                        )
                                        .map((imageElement) => (
                                            <Card
                                                key={imageElement.id}
                                                className={cn(
                                                    "p-2 cursor-pointer transition-all duration-200",
                                                    selectedImageId === imageElement.id
                                                        ? "ring-1 ring-primary"
                                                        : "hover:bg-muted/50",
                                                )}
                                                onClick={() =>
                                                    handleElementSelection("image", imageElement.id)
                                                }
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium truncate">
                                                        {imageElement.content
                                                            ? "Image Element"
                                                            : "No Image"}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setElements((prev) =>
                                                                prev.filter(
                                                                    (el) =>
                                                                        el.id !== imageElement.id,
                                                                ),
                                                            );
                                                            setSelectedImageId(null);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                {renderImageControls(imageElement)}
                                            </Card>
                                        ))}
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <Label>Container Elements</Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleAddContainer}
                                    >
                                        Add Container
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {elements
                                        .filter(
                                            (el): el is TemplateElement & { type: "container" } =>
                                                el.type === "container",
                                        )
                                        .map((container) => (
                                            <Card
                                                key={container.id}
                                                className={cn(
                                                    "p-2 cursor-pointer transition-all duration-200",
                                                    selectedContainerId === container.id
                                                        ? "ring-1 ring-primary"
                                                        : "hover:bg-muted/50",
                                                )}
                                                onClick={() =>
                                                    handleElementSelection(
                                                        "container",
                                                        container.id,
                                                    )
                                                }
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">
                                                        Container
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setElements((prev) =>
                                                                prev.filter(
                                                                    (el) => el.id !== container.id,
                                                                ),
                                                            );
                                                            setSelectedContainerId(null);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                {selectedContainerId === container.id &&
                                                    renderContainerControls(container)}
                                            </Card>
                                        ))}
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <Label>Text Elements</Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleAddTextElement}
                                    >
                                        Add Text
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {elements
                                        .filter(
                                            (el): el is TemplateElement & { type: "text" } =>
                                                el.type === "text",
                                        )
                                        .map((textElement) => {
                                            const isSelected = selectedTextIds.includes(
                                                textElement.id!,
                                            );
                                            return (
                                                <Card
                                                    key={textElement.id}
                                                    className={cn(
                                                        "p-2 cursor-pointer transition-all duration-200",
                                                        isSelected
                                                            ? "ring-1 ring-primary"
                                                            : "hover:bg-muted/50",
                                                    )}
                                                    onClick={(e) =>
                                                        handleTextElementClick(textElement.id!, e)
                                                    }
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <Input
                                                            value={textElement.content}
                                                            onChange={(e) => {
                                                                setElements((prev) =>
                                                                    prev.map((el) =>
                                                                        el.id === textElement.id
                                                                            ? {
                                                                                  ...el,
                                                                                  content:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : el,
                                                                    ),
                                                                );
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleElementSelection(
                                                                    "text",
                                                                    textElement.id,
                                                                );
                                                            }}
                                                            className="flex-1 mr-2 h-8"
                                                            placeholder="Enter text"
                                                        />
                                                        <div className="flex gap-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setElements((prev) =>
                                                                        prev.filter(
                                                                            (el) =>
                                                                                el.id !==
                                                                                textElement.id,
                                                                        ),
                                                                    );
                                                                    setSelectedTextIds((prev) =>
                                                                        prev.filter(
                                                                            (id) =>
                                                                                id !==
                                                                                textElement.id,
                                                                        ),
                                                                    );
                                                                }}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <div className="space-y-4 mt-4">
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="space-y-2">
                                                                    <Label className="text-xs">
                                                                        Text Style
                                                                    </Label>
                                                                    <div className="flex gap-1 items-center">
                                                                        <Button
                                                                            variant={
                                                                                textElement.style
                                                                                    .fontWeight ===
                                                                                "bold"
                                                                                    ? "default"
                                                                                    : "outline"
                                                                            }
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                toggleTextStyle(
                                                                                    textElement.id,
                                                                                    "fontWeight",
                                                                                    "bold",
                                                                                )
                                                                            }
                                                                            className="h-8 w-8"
                                                                        >
                                                                            B
                                                                        </Button>
                                                                        <Button
                                                                            variant={
                                                                                textElement.style
                                                                                    .fontStyle ===
                                                                                "italic"
                                                                                    ? "default"
                                                                                    : "outline"
                                                                            }
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                toggleTextStyle(
                                                                                    textElement.id,
                                                                                    "fontStyle",
                                                                                    "italic",
                                                                                )
                                                                            }
                                                                            className="h-8 w-8"
                                                                        >
                                                                            I
                                                                        </Button>
                                                                        <Button
                                                                            variant={
                                                                                textElement.style
                                                                                    .textDecoration ===
                                                                                "underline"
                                                                                    ? "default"
                                                                                    : "outline"
                                                                            }
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                toggleTextStyle(
                                                                                    textElement.id,
                                                                                    "textDecoration",
                                                                                    "underline",
                                                                                )
                                                                            }
                                                                            className="h-8 w-8"
                                                                        >
                                                                            U
                                                                        </Button>
                                                                        <div className="flex gap-2 items-center">
                                                                            <Input
                                                                                type="color"
                                                                                className="w-12 h-8 p-1 rounded-md"
                                                                                value={
                                                                                    textElement.style.color?.toString() ||
                                                                                    "#000000"
                                                                                }
                                                                                onChange={(e) => {
                                                                                    setElements(
                                                                                        (prev) =>
                                                                                            prev.map(
                                                                                                (
                                                                                                    el,
                                                                                                ) =>
                                                                                                    el.id ===
                                                                                                    textElement.id
                                                                                                        ? {
                                                                                                              ...el,
                                                                                                              style: {
                                                                                                                  ...el.style,
                                                                                                                  color: e
                                                                                                                      .target
                                                                                                                      .value,
                                                                                                              },
                                                                                                          }
                                                                                                        : el,
                                                                                            ),
                                                                                    );
                                                                                }}
                                                                            />
                                                                            <span className="text-xs text-muted-foreground">
                                                                                {textElement.style.color?.toString() ||
                                                                                    "#000000"}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs">
                                                                        Font Size
                                                                    </Label>
                                                                    <Input
                                                                        type="range"
                                                                        min="0.5"
                                                                        max="15"
                                                                        step="0.2"
                                                                        value={getFontSize(
                                                                            textElement.style,
                                                                        )}
                                                                        onChange={(e) => {
                                                                            const newSize =
                                                                                parseFloat(
                                                                                    e.target.value,
                                                                                );
                                                                            setElements((prev) =>
                                                                                prev.map((el) =>
                                                                                    el.id ===
                                                                                    textElement.id
                                                                                        ? {
                                                                                              ...el,
                                                                                              style: {
                                                                                                  ...el.style,
                                                                                                  fontSize: `${newSize}em`,
                                                                                              },
                                                                                          }
                                                                                        : el,
                                                                                ),
                                                                            );
                                                                        }}
                                                                        className="h-2"
                                                                    />
                                                                    <div className="text-xs text-muted-foreground">
                                                                        Size:{" "}
                                                                        {getFontSize(
                                                                            textElement.style,
                                                                        ).toFixed(1)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="flex-1 flex items-start justify-center rounded-lg overflow-hidden">
                <div className={styles.previewSection}>
                    <div className={styles.templateWrapper}>
                        <ScaledPreview>
                            <div
                                ref={previewAreaRef}
                                className={styles.templateContent}
                                onClick={() => {
                                    setSelectedContainerId(null);
                                    setSelectedTextIds([]);
                                }}
                                onMouseMove={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleMouseMove(e.nativeEvent);
                                }}
                                onMouseUp={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleMouseUp();
                                }}
                            >
                                {/* Background color is rendered separately and is always at the bottom */}
                                <div
                                    className="absolute inset-0"
                                    style={{ backgroundColor: templateStyle.backgroundColor }}
                                />

                                {/* Sort and render elements by z-index */}
                                {[...elements]
                                    .sort(
                                        (a, b) =>
                                            (Number(a.style?.zIndex) || 0) -
                                            (Number(b.style?.zIndex) || 0),
                                    )
                                    .map((element) => {
                                        if (element.type === "image") {
                                            return renderImageElement(element);
                                        }

                                        if (element.type === "container") {
                                            return renderContainerElement(element);
                                        }

                                        if (element.type === "text") {
                                            return renderTextElement(element);
                                        }

                                        return null;
                                    })}
                            </div>
                        </ScaledPreview>
                    </div>
                </div>
            </div>
        </div>
    );
};
